<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import { useConfigStore, useLoadingStore, useToastStore } from '@/stores'
import { AD_FINISH_ACTION, STORAGE_CUSTOM_THEME } from '@/constants'
import { KS } from '@/kuaishou'
import { $log } from '@/helpers'
import { useLocalAdFree } from '@/composables'
import { track } from '@/track'

const props = withDefaults(defineProps<{
  value: boolean
  sessionId: string
  src: string
  poster?: string
  caption?: string
  isDownload?: boolean
  actionBar: string
  actionBarColor: string
  receiveCoin: boolean
  contentId: string
  chapterId: string
}>(), {
  isDownload: false,
  receiveCoin: true,
})

const emit = defineEmits<{
  (event: 'input', value: boolean): void
  (event: 'end'): void
  (event: 'cancel'): void
  (event: 'error'): void
}>()

const theme = useLocalStorage(STORAGE_CUSTOM_THEME, {
  background: '#FFF',
  size: 'md' as 'sm' | 'md' | 'lg' | 'xl',
})

const configStore = useConfigStore()
const { $toast } = useToastStore()
const { $showLoading, $hideLoading } = useLoadingStore()

const countdowning = ref(true)
const autoNext = computed(() => {
  return configStore.$state.playBackStatus === AD_FINISH_ACTION.NEXT
})
function onCountdownEnd() {
  countdowning.value = false
}

const videoRef = ref<HTMLVideoElement>()
const videoPlayer = ref(true)
const videoCountdown = ref(0)
let report3s = false
let report5s = false
function onVideoLoad() {
  KS.adVideoTrack(props.sessionId, 'startPlay')
  videoRef.value?.addEventListener('ended', () => {
    $log('Component BizAdVideoPlayer', '#timeupdate', 'ended')
    emit('end')
    KS.adVideoTrack(props.sessionId, 'playEnd')
  })
  videoRef.value?.addEventListener('timeupdate', () => {
    if (!videoRef.value)
      return
    videoCountdown.value = Math.round(videoRef.value.duration - videoRef.value.currentTime)
    if (!report3s && videoRef.value.currentTime >= 3) {
      $log('Component BizAdVideoPlayer', '#timeupdate', 'played3s')
      KS.adVideoTrack(props.sessionId, 'played3s')
      report3s = true
    }
    if (!report5s && videoRef.value.currentTime >= 5) {
      $log('Component BizAdVideoPlayer', '#timeupdate', 'played5s')
      KS.adVideoTrack(props.sessionId, 'played5s')
      report5s = true
    }
  })
  videoRef.value?.addEventListener('error', (error) => {
    console.error(error)
    emit('error')
    $toast('广告视频加载失败')
    emit('input', false)
  })
}
const videoMute = ref(true)
function onVideoVolumeToggle() {
  videoMute.value = !videoMute.value
  videoRef.value!.muted = videoMute.value
}

watch(() => props.sessionId, (val, oldVal) => {
  report3s = false
  report5s = false
  KS.adVideoTrack(val, 'show')
  KS.adVideoDestroy(oldVal)
})
onMounted(() => {
  KS.adVideoTrack(props.sessionId, 'show')
})
onUnmounted(() => {
  KS.adVideoDestroy(props.sessionId)
})

function openAdDetail() {
  KS.openAdVideoDetail(props.sessionId)
}

const adVideoPlayer = ref(false)
const adVideoData = ref<any>({})
const { setEnableMinutes } = useLocalAdFree()
async function onLoadAdVideoForFree() {
  $log('Component: BizAdVideoPlayer', '#onLoadAdVideoForFree')
  $showLoading()
  try {
    track('inspireVideo', { name: 'freeAd', type: 'start' })
    await KS.playAdVideo({
      type: 1,
      success(data: any) {
        track('inspireVideo', { name: 'freeAd', type: 'end' })
        $log('Component: BizAdVideoPlayer', '#onLoadAdVideoForFree ~> success', { data })
        $toast(`看完广告视频, 可免 ${configStore.$state.sourceFreeTime} 分钟广告`)
        setEnableMinutes(configStore.$state.freeTime)
        emit('input', false)
      },
      fail(error: any) {
        track('inspireVideo', { name: 'freeAd', type: 'cancel' })
        $log('Component: BizAdVideoPlayer', '#onLoadAdVideoForFree ~> fail', { error })
        $toast('未看完视频，无法领取奖励')
        emit('input', false)
      },
    })
    // videoPlayer.value = false
  }
  catch {}
  finally {
    $hideLoading()
  }
}

function onCancel() {
  emit('cancel')
  emit('input', false)
}

function onEndAdVideo() {
  adVideoPlayer.value = false
  setEnableMinutes(configStore.$state.freeTime)
  $toast(`看完广告视频, 可免 ${configStore.$state.sourceFreeTime} 分钟广告`, 3000)
  emit('input', false)
}

defineExpose({
  videoRef,
})
</script>

<template>
  <div v-if="value">
    <div
      v-if="videoPlayer" class="px-10 flex flex-col justify-center fixed inset-0 z-100"
      :style="{ background: theme.background }"
    >
      <p class="flex items-center justify-center text-17px text-#333">
        <span>广告是为了更好的支持正版</span>
      </p>
      <div class="mt-6.5 w-260px mx-a relative rounded-lg overflow-hidden">
        <video
          v-if="src"
          ref="videoRef"
          :poster="poster" :src="src.replace('http://', 'https://')"
          autoplay muted
          class="w-full z-1001 aspect-9/16 bg-black"
          @loadeddata="onVideoLoad"
        />
        <span class="text-white absolute top-2 left-2 px-2 py-0.5 text-xs rounded-tl-2 rounded-br-2 bg-#00000060">广告</span>
        <span v-if="receiveCoin && videoCountdown" class="text-white absolute top-2 left-15 px-2 py-0.5 text-xs rounded-lg bg-#00000060">{{ videoCountdown }}秒后可领取奖励</span>
        <div class="text-white absolute top-2 right-2" @click="onVideoVolumeToggle">
          <i-icon-park-outline-volume-mute v-if="videoMute" />
          <i-icon-park-outline-volume-notice v-else />
        </div>
        <div class="w-full p-4 absolute bottom-0 bg-gradient-to-b from-transparent to-#000000cc">
          <p v-if="caption" class="w-full text-white">
            {{ caption }}
          </p>
          <div v-if="isDownload" class="mt-4 w-full">
            <BizTrackClick
              name="insertAd" type="button" :extra="{ contentId, chapterId, sessionId, type: 'video' }"
              class="block w-full py-1.5 font-bold text-17px text-center text-white rounded-full shadow-2xl"
              :style="{ backgroundColor: `#${actionBarColor}` }"
              @click="openAdDetail"
            >
              {{ actionBar }}
            </BizTrackClick>
          </div>
        </div>
      </div>

      <p v-if="!receiveCoin && configStore.$state.freeTime" class="mt-4 flex items-center justify-center text-primary text-center" @click="onLoadAdVideoForFree">
        <span>看视频免{{ configStore.$state.sourceFreeTime }}分钟广告</span>
        <i-icon-park-outline-right class="mt-0.5" />
      </p>

      <div class="fixed w-full left-0 bottom-8 text-center text-sm text-#999">
        <p v-if="countdowning">
          <BasicCountdown :seconds="configStore.$state.pageDwellTime" @end="onCountdownEnd">
            <template #default="{ seconds }">
              {{ seconds }} 秒后可继续阅读下一页
            </template>
          </BasicCountdown>
        </p>
        <BizTrackClick
          v-else name="insertAdClose" type="button"
          :extra="{ contentId, chapterId, sessionId, type: 'video' }"
          @click="onCancel"
        >
          {{ receiveCoin ? '放弃奖励,' : '' }} 继续阅读下一章
        </BizTrackClick>
      </div>
    </div>

    <!-- <BizAdVideoPlayerForFree
      v-if="adVideoPlayer"
      v-model="adVideoPlayer" :session-id="adVideoData.sessionId"
      :src="adVideoData.src" :poster="adVideoData.poster" :caption="adVideoData.caption"
      :is-download="adVideoData.isDownload" :action-bar="adVideoData.actionBar" :action-bar-color="adVideoData.actionBarColor"
      @end="onEndAdVideo"
    /> -->
  </div>
</template>
